export interface TaxFormulaValidationResult {
    isValid: boolean
    errorMessage?: string
}

export const validateTaxFormulaExpression = (
    expression: string,
    placeholders: string[] = []
): TaxFormulaValidationResult => {
    if (!expression || expression.trim() === '') {
        return {
            isValid: false,
            errorMessage: 'Expression cannot be empty',
        }
    }

    // Remove spaces for validation
    const sanitized = expression.replace(/\s+/g, '')

    // 1. Check for consecutive operators
    if (/[+\-*/]{2,}/.test(sanitized)) {
        return {
            isValid: false,
            errorMessage:
                'Consecutive operators are not allowed (e.g., ++, **, //)',
        }
    }

    // 2. Check parentheses balance
    let stack = 0
    for (const ch of sanitized) {
        if (ch === '(') stack++
        if (ch === ')') stack--
        if (stack < 0) {
            return {
                isValid: false,
                errorMessage:
                    'Mismatched parentheses: closing parenthesis without opening',
            }
        }
    }
    if (stack !== 0) {
        return {
            isValid: false,
            errorMessage:
                'Mismatched parentheses: unclosed opening parenthesis',
        }
    }

    // Normalize placeholders - extract names from wrapped placeholders
    const normalizedPlaceholders = placeholders.map((placeholder) =>
        placeholder.startsWith('{{') && placeholder.endsWith('}}')
            ? placeholder.slice(2, -2)
            : placeholder
    )

    // 3. Validate placeholders if provided
    if (placeholders.length > 0) {
        // Find all potential placeholders in the expression (wrapped in {{}})
        const placeholderPattern = normalizedPlaceholders
            .map(
                (placeholder) =>
                    `\\{\\{${placeholder.replace(
                        /[.*+?^${}()|[\]\\]/g,
                        '\\$&'
                    )}\\}\\}`
            )
            .join('|')

        const wordsInExpression =
            sanitized.match(new RegExp(placeholderPattern, 'g')) || []

        // Check that all placeholders in the expression are valid
        for (const word of wordsInExpression) {
            // Extract the placeholder name from {{placeholder}}
            const placeholderName = word.replace(/^\{\{|\}\}$/g, '')
            if (!normalizedPlaceholders.includes(placeholderName)) {
                return {
                    isValid: false,
                    errorMessage: `Invalid placeholder: "${word}". Please enter valid placeholders`,
                }
            }
        }

        // Create pattern that includes valid placeholders wrapped in {{}}
        const validPattern = new RegExp(
            `^(${placeholderPattern}|[0-9+\\-*/().\\s])*$`
        )

        if (!validPattern.test(sanitized)) {
            return {
                isValid: false,
                errorMessage:
                    'Expression contains invalid characters. Only numbers, operators (+, -, *, /), parentheses, and valid placeholders are allowed',
            }
        }
    } else {
        // Original pattern for backward compatibility
        const validPattern = /^[0-9+\-*/().\s]+$/
        if (!validPattern.test(expression)) {
            return {
                isValid: false,
                errorMessage:
                    'Expression contains invalid characters. Only numbers, operators (+, -, *, /), parentheses, and dots are allowed',
            }
        }
    }

    try {
        // 4. Try to evaluate the expression structure
        // Replace placeholders with dummy values for evaluation
        let evaluationExpression = sanitized
        for (const placeholder of normalizedPlaceholders) {
            evaluationExpression = evaluationExpression.replace(
                new RegExp(
                    `\\{\\{${placeholder.replace(
                        /[.*+?^${}()|[\]\\]/g,
                        '\\$&'
                    )}\\}\\}`,
                    'g'
                ),
                '1'
            )
        }

        // Additional validation: Check for trailing operators
        if (/[+\-*/]$/.test(evaluationExpression.trim())) {
            return {
                isValid: false,
                errorMessage: 'Expression cannot end with an operator',
            }
        }

        // Additional validation: Check for leading operators (except minus)
        if (/^[+*/]/.test(evaluationExpression.trim())) {
            return {
                isValid: false,
                errorMessage:
                    'Expression cannot start with an operator (except minus)',
            }
        }

        // Additional validation: Check for empty expression
        if (evaluationExpression.trim() === '') {
            return {
                isValid: false,
                errorMessage: 'Expression cannot be empty',
            }
        }

        // Evaluate in a safe context
        Function(`return (${evaluationExpression})`)()

        return {
            isValid: true,
        }
    } catch (error) {
        return {
            isValid: false,
            errorMessage: 'Invalid mathematical expression structure',
        }
    }
}

export const parseFormula = (
    formulaString: string,
    taxPlaceholders: string[]
): string[] => {
    if (!formulaString) return []

    const parts: string[] = []
    let currentPart = ''

    for (let i = 0; i < formulaString.length; i++) {
        const char = formulaString[i]

        // Check if we're starting a placeholder wrapped in {{}}
        if (char === '{' && formulaString[i + 1] === '{') {
            let placeholderFound = false
            for (const placeholder of taxPlaceholders) {
                // Handle both cases: placeholder already wrapped in {{}} or just the name
                const wrappedPlaceholder =
                    placeholder.startsWith('{{') && placeholder.endsWith('}}')
                        ? placeholder
                        : `{{${placeholder}}}`

                if (
                    formulaString.substring(
                        i,
                        i + wrappedPlaceholder.length
                    ) === wrappedPlaceholder
                ) {
                    if (currentPart) {
                        parts.push(currentPart)
                        currentPart = ''
                    }
                    parts.push(wrappedPlaceholder)
                    i += wrappedPlaceholder.length - 1 // Skip the rest of the placeholder
                    placeholderFound = true
                    break
                }
            }

            if (placeholderFound) {
                continue
            }
        }

        // Check if it's an operator
        if (['+', '-', '*', '/', '(', ')'].includes(char)) {
            if (currentPart) {
                parts.push(currentPart)
                currentPart = ''
            }
            parts.push(char)
            continue
        }

        // Add character to current part
        currentPart += char
    }

    // Add any remaining part
    if (currentPart) {
        parts.push(currentPart)
    }

    return parts.filter((part) => part.trim() !== '')
}

export const getFormattedTaxDetailName = (name: string) => {
    return name
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/[\s-]+/g, '_')
        .toLowerCase()
}
